import { NiceChannelBox } from '@/features/analytics/components/nice-channel-box'
import { CreativeBadge } from '@/features/creative/creative-select'
import { ICreative } from '@/shared/api'
import { QuestionSVGIcon } from '@/shared/assets'
import {
    Button,
    Checkbox,
    Dialog,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DisableTooltip,
    RubleIconInline,
} from '@/shared/ui'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip'
import { cn } from '@/true-fsd/shared/utils/css'
import { DialogTrigger } from '@radix-ui/react-dialog'
import { VkOrdCreativeResponseWithCreative } from '@synapex/shared2/gateway-rest-api'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import { pluralize } from '@synapex/shared2/utils/string'
import clsx from 'clsx'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

const TableRow = ({
    creative,
    linkName,
    isSelected,
    handleToggleCreative,
    shouldRenderCheckbox = true,
}: {
    creative: VkOrdCreativeResponseWithCreative
    linkName?: string
    isSelected: boolean
    handleToggleCreative?: (externalId: string) => void
    shouldRenderCheckbox?: boolean
}) => {
    return (
        <tr className="flex items-center gap-2 py-1">
            <td className="w-[141px]">
                <NiceChannelBox
                    {...parseTelegramPeerReference(linkName || '')}
                    title={
                        creative.siteLink?.external_channel
                            ?.telegram_channel_name
                    }
                    toastClassName="toast__marking-modal"
                    className={clsx(
                        'name-cell__channel-box',
                        'w-full',
                        'flex items-center'
                    )}
                    photoUrl={
                        creative.siteLink?.external_channel
                            ?.small_photo_data_url
                    }
                    rknStatus={
                        creative.siteLink?.external_channel?.rkn_status ??
                        undefined
                    }
                />
            </td>
            <td className="w-[141px]">
                <CreativeBadge
                    data={
                        creative.creative as unknown as Omit<
                            ICreative,
                            'active_in_site_links' | 'active_in_invite_links'
                        >
                    }
                />
            </td>
            <td className="w-[53px] text-xs">{creative.budget} ₽</td>
            <td className="w-[98px]">
                {shouldRenderCheckbox && (
                    <Checkbox
                        checked={isSelected}
                        onChange={() =>
                            handleToggleCreative?.(creative.external_id)
                        }
                    />
                )}
            </td>
        </tr>
    )
}

export const AgencyCreativePanel = ({
    vkOrdCreatives,
    selectedCreativeIds,
    onSelectionChange,
    disabled,
    errorKey = 0,
}: {
    vkOrdCreatives: VkOrdCreativeResponseWithCreative[]
    selectedCreativeIds: string[]
    onSelectionChange: (selectedIds: string[], totalBudget: number) => void
    disabled: boolean
    errorKey?: number
}) => {
    const error = errorKey > 0
    const [open, setOpen] = useState(false)
    const [draftSelectedIds, setDraftSelectedIds] = useState<string[]>([])
    const allCreatives = vkOrdCreatives

    const handleOpenChange = (nextOpen: boolean) => {
        if (nextOpen) {
            setDraftSelectedIds([...selectedCreativeIds])
        }
        setOpen(nextOpen)
    }

    const handleSave = () => {
        onSelectionChange(draftSelectedIds, totalBudget)
        setOpen(false)
    }

    const handleToggleCreative = (externalId: string) => {
        if (draftSelectedIds.includes(externalId)) {
            setDraftSelectedIds(
                draftSelectedIds.filter((id) => id !== externalId)
            )
        } else {
            setDraftSelectedIds([...draftSelectedIds, externalId])
        }
    }

    const readyCreatives = allCreatives.filter(
        (c) => c.siteLink?.published_at || c.inviteLink?.published_at
    )

    const handleToggleAll = () => {
        if (readyCreatives.length === 0) {
            toast.error('Нет доступных креативов для выбора')
            return
        }

        if (draftSelectedIds.length === readyCreatives.length) {
            setDraftSelectedIds([])
        } else {
            setDraftSelectedIds(readyCreatives.map((c) => c.external_id))
        }
    }

    const totalBudget = draftSelectedIds.reduce(
        (acc, creativeId) =>
            acc +
            (allCreatives.find((c) => c.external_id === creativeId)?.budget ??
                0),
        0
    )

    const notReadyCreatives = useMemo(() => {
        return allCreatives.filter(
            (c) => !c.siteLink?.published_at && !c.inviteLink?.published_at
        )
    }, [allCreatives])

    const renderTable = (
        creatives: VkOrdCreativeResponseWithCreative[],
        shouldRenderCheckbox = true
    ) => {
        if (creatives.length === 0) {
            return (
                <div className="flex w-full items-center justify-center py-10 text-sm text-synapex-light-gray">
                    Нет доступных креативов
                </div>
            )
        }

        return (
            <tbody>
                {creatives.map((creative) => {
                    const key = creative.external_id
                    let linkName
                    if (creative.siteLink) {
                        linkName = creative.siteLink.site_link_name
                    } else {
                        linkName = creative.inviteLink?.invite_link_name
                    }

                    const isSelected = draftSelectedIds.includes(
                        creative.external_id
                    )

                    return (
                        <TableRow
                            key={key}
                            creative={creative}
                            isSelected={isSelected}
                            linkName={linkName}
                            handleToggleCreative={handleToggleCreative}
                            shouldRenderCheckbox={shouldRenderCheckbox}
                        />
                    )
                })}
            </tbody>
        )
    }

    return (
        <Dialog modal open={open} onOpenChange={handleOpenChange}>
            {disabled ? (
                <DisableTooltip
                    message="Чтобы выбрать креативы необходимо выбрать автоматический расчет бюджета"
                    enabled
                    className="flex-grow"
                >
                    <button
                        key={errorKey}
                        type="button"
                        className={cn(
                            'w-full',
                            'flex items-center justify-between rounded-md border px-3 py-2 text-[12px]',
                            'h-[30px]',
                            '!cursor-not-allowed opacity-70',
                            'transition-colors',
                            error
                                ? 'animate-shake border-red-500'
                                : 'border-[#CCCCCC]'
                        )}
                    >
                        Выбрать креативы <span className="text-lg">+</span>
                    </button>
                </DisableTooltip>
            ) : (
                <DialogTrigger asChild key={errorKey}>
                    <button
                        type="button"
                        className={cn(
                            'flex-grow',
                            'flex items-center justify-between rounded-md border px-3 py-2 text-[12px]',
                            'h-[30px]',
                            'transition-colors',
                            error
                                ? 'animate-shake border-red-500'
                                : 'border-[#CCCCCC]'
                        )}
                    >
                        {pluralize(
                            selectedCreativeIds.length,
                            'Выбран',
                            'Выбрано',
                            'Выбрано'
                        )}{' '}
                        {selectedCreativeIds.length}{' '}
                        {pluralize(
                            selectedCreativeIds.length,
                            'креатив',
                            'креатива',
                            'креативов'
                        )}
                    </button>
                </DialogTrigger>
            )}

            <DialogContent
                aria-describedby={undefined}
                className="top-1/3 w-[505px] translate-y-[-30%] gap-0 overflow-y-auto bg-white p-6 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.15)]"
            >
                <DialogHeader className="w-full justify-center pb-4 text-xs font-black">
                    <DialogTitle className="flex w-full items-center">
                        <div className="flex items-center gap-3">
                            <span>Доступные креативы</span>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                <div className="pb-4">
                    <div className="flex items-center gap-2">
                        <div className="flex w-[159px] flex-col gap-[6px]">
                            <span className="text-[10px] text-synapex-light-gray">
                                Стоимость итого
                            </span>
                            <div className="flex items-center gap-1 rounded-[5px] border px-[10px] py-2 text-xs">
                                {totalBudget}
                                <RubleIconInline className="mb-0" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 max-h-[500px] overflow-y-auto overflow-x-hidden">
                    <table className="w-full">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="flex gap-2 text-[10px] text-black/40">
                                <th className="w-[141px] text-start">
                                    Площадка
                                </th>
                                <th className="w-[141px] text-start">
                                    Креатив
                                </th>
                                <th className="w-[53px] text-start">Цена</th>
                                <th className="w-[98px] text-nowrap text-start">
                                    <div className="flex items-center gap-1">
                                        <Checkbox
                                            checked={
                                                draftSelectedIds.length ===
                                                    readyCreatives.length &&
                                                readyCreatives.length > 0
                                            }
                                            onChange={handleToggleAll}
                                        />
                                        <span>Выбрать все</span>
                                    </div>
                                </th>
                            </tr>
                        </thead>

                        {renderTable(readyCreatives)}

                        <thead>
                            <tr>
                                <th colSpan={100}>
                                    <h2 className="flex w-full items-center py-2 text-lg font-semibold leading-none tracking-tight">
                                        <div className="flex items-center gap-1">
                                            <span>Ожидание публикации</span>
                                            <Tooltip>
                                                <TooltipTrigger type="button">
                                                    <QuestionSVGIcon className="cursor-pointer" />
                                                </TooltipTrigger>
                                                <TooltipContent
                                                    side="top"
                                                    className="max-w-56 text-center"
                                                >
                                                    <p>
                                                        Непубликованные креативы
                                                        нельзя включить в акт —
                                                        они станут доступны
                                                        после публикации.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </div>
                                    </h2>
                                </th>
                            </tr>
                        </thead>

                        {renderTable(notReadyCreatives, false)}
                    </table>
                </div>
                <DialogFooter className="justify-between gap-4 pt-5">
                    <DialogClose asChild>
                        <Button className="m-0 w-full" variant="secondary">
                            Отмена
                        </Button>
                    </DialogClose>

                    <Button className="m-0 w-full" onClick={handleSave}>
                        Сохранить
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
